/**
 * Post-cook pantry cleanup helpers (binary remove vs keep; no quantity math).
 */

export const PANTRY_CLEANUP_REQUEST_EVENT = 'pantry-cleanup-request'
export const PANTRY_INVENTORY_CHANGED_EVENT = 'pantry-inventory-changed'
export const OPEN_PANTRY_RECEIPT_EVENT = 'open-pantry-receipt'

/**
 * @typedef {object} PantryCleanupRow
 * @property {string} id
 * @property {string} pantryName
 * @property {string[]} ingredientNames
 * @property {boolean} selected
 */

/**
 * @param {Array<{ matched_pantry_id?: string | null, matched_pantry_name?: string | null, name?: string, default_remove?: boolean }>} ingredients
 * @returns {PantryCleanupRow[]}
 */
export function buildPantryCleanupRows(ingredients) {
  /** @type {Map<string, PantryCleanupRow>} */
  const byId = new Map()
  for (const ing of ingredients || []) {
    const id = ing?.matched_pantry_id
    if (!id) continue
    const pantryName = (ing.matched_pantry_name || '').trim()
    const ingredientName = (ing.name || '').trim()
    const removeByDefault = Boolean(ing.default_remove)
    const existing = byId.get(id)
    if (!existing) {
      byId.set(id, {
        id,
        pantryName,
        ingredientNames: ingredientName ? [ingredientName] : [],
        selected: removeByDefault,
      })
      continue
    }
    if (ingredientName && !existing.ingredientNames.includes(ingredientName)) {
      existing.ingredientNames.push(ingredientName)
    }
    if (!removeByDefault) {
      existing.selected = false
    }
  }
  return [...byId.values()].sort((a, b) => a.pantryName.localeCompare(b.pantryName))
}

/**
 * @param {PantryCleanupRow[]} rows
 * @returns {string[]}
 */
export function selectedPantryCleanupIds(rows) {
  return (rows || []).filter((row) => row.selected).map((row) => row.id)
}

export function notifyPantryInventoryChanged() {
  try {
    window.dispatchEvent(new CustomEvent(PANTRY_INVENTORY_CHANGED_EVENT))
  } catch {
    /* ignore */
  }
}

export function dispatchOpenPantryReceipt() {
  try {
    window.dispatchEvent(new CustomEvent(OPEN_PANTRY_RECEIPT_EVENT))
  } catch {
    /* ignore */
  }
}

/**
 * Open the global pantry cleanup sheet after a cook event.
 *
 * @param {{
 *   recipeId: string,
 *   recipeTitle?: string,
 *   preparationId?: string | null,
 *   servings?: number | null,
 * }} payload
 * @returns {Promise<void>} resolves when the sheet closes (apply or skip)
 */
export function requestPantryCleanupAfterCook(payload) {
  return new Promise((resolve) => {
    try {
      window.dispatchEvent(
        new CustomEvent(PANTRY_CLEANUP_REQUEST_EVENT, {
          detail: { ...payload, resolve },
        }),
      )
    } catch {
      resolve()
    }
  })
}
